import type { RateGrid } from "@redotech/admin-sdk/rpc/schema/rate-tables/rate-grid-schema";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { memo } from "react";
import * as styles from "./rate-grid-preview.module.css";

interface RateGridPreviewProps {
  rateGrid: RateGrid;
  maxRows?: number;
  maxCols?: number;
}

export const RateGridPreview = memo(function RateGridPreview({
  rateGrid,
  maxRows = 10,
  maxCols = 8,
}: RateGridPreviewProps) {
  const displayedWeights = rateGrid.weightBucketsOz.slice(0, maxRows);
  const displayedZones = rateGrid.zones.slice(0, maxCols);
  const hasMoreRows = rateGrid.weightBucketsOz.length > maxRows;
  const hasMoreCols = rateGrid.zones.length > maxCols;

  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="sm" fontWeight="medium">
        Rate Grid Preview ({rateGrid.weightBucketsOz.length} weights x{" "}
        {rateGrid.zones.length} zones)
      </Text>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.headerCell}>Weight (oz)</th>
              {displayedZones.map((zone) => (
                <th className={styles.headerCell} key={zone}>
                  Zone {zone}
                </th>
              ))}
              {hasMoreCols && <th className={styles.headerCell}>...</th>}
            </tr>
          </thead>
          <tbody>
            {displayedWeights.map((weight, wi) => (
              <tr key={weight}>
                <td className={styles.rowHeader}>{weight}</td>
                {displayedZones.map((zone, zi) => {
                  const price = rateGrid.priceGrid[wi]?.[zi];
                  const count = rateGrid.countGrid[wi]?.[zi] ?? 0;
                  return (
                    <td
                      className={styles.cell}
                      key={zone}
                      title={`${count} orders`}
                    >
                      {price != null ? `$${price.toFixed(2)}` : "-"}
                    </td>
                  );
                })}
                {hasMoreCols && <td className={styles.cell}>...</td>}
              </tr>
            ))}
            {hasMoreRows && (
              <tr>
                <td className={styles.rowHeader}>...</td>
                {displayedZones.map((zone) => (
                  <td className={styles.cell} key={zone}>
                    ...
                  </td>
                ))}
                {hasMoreCols && <td className={styles.cell}>...</td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Flex>
  );
});
